<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/tours/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :row-link="(reg) => ({ name: 'driver-details', params: { driverId: reg?.driver.id } })"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, type Ref, unref} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, {type Column} from '@utils/table/Table.vue';
import {type Filter as ProviderFilter, useProvider} from '@utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter} from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {CampaignGroup, HydraCollection, Tour} from '@/entities/Entities';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<ProviderFilter>('filter', {});
const fixedFilter = computed(() => ({
  'campaignRegistration.campaign.group': props.modelValue?.id ?? '',
  'inArray[campaignRegistration.campaign.region]': globalFilter?.value?.regions,
}));

const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.toursFromGroup', {
    group: props.modelValue?.name,
  });
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: true,
  },
  {
    key: 'createdAt',
    title: t('tour.createdAt'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        label: t('tour.createdAt'),
      },
    },
  },
  {
    key: 'uploadedAt',
    title: t('uploadedAt'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        label: t('uploadedAt'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaignRegistration.campaign',
      type: 'dropdown',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaignRegistration.campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'campaignRegistration.campaign.timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (tour: Tour) => {
      const now = new Date();
      if (!tour.campaignRegistration) {
        return '';
      }
      const startDate = new Date(tour.campaignRegistration?.campaign.actualStartDate);
      const endDate = new Date(tour.campaignRegistration?.campaign.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      key: 'timeStatus',
      type: 'dropdown',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'distance',
    title: t('tour.distance'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('tour.distance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'time',
    title: t('tour.time'),
    group: t('pages.campaigns.tourList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.campaigns.tourList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('tour.time'),
      },
    },
  },
  {
    key: 'averageSpeed',
    title: t('tour.speed'),
    group: t('pages.campaigns.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => (v * 3.6).toFixed(2) + ' km/h',
    filter: {
      type: 'range',
      group: t('pages.campaigns.tourList.filterGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('tour.speed'),
        step: 'any',
        min: '0',
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'kcal',
    title: t('tour.kcal'),
    group: t('pages.campaigns.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    filter: {
      type: 'range',
      group: t('pages.campaigns.tourList.filterGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('tour.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('tour.co2'),
    group: t('pages.campaigns.tourList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kg',
    filter: {
      type: 'range',
      group: t('pages.campaigns.tourList.filterGroups.calculations'),
      options: {
        icon: <span>t</span>,
        label: t('tour.co2'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const { data: items, loading } = useProvider<HydraCollection<Tour>>('/tours', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
